import { useCallback, useState } from "react"

import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import type { Persona } from "@/types/api/Persona"
import { PersonaDTO } from "@/utils/persona/persona-dto"

import { isBuiltInPersona } from "../components/persona-utils"
import { getBackgroundSoundSelectionPatchAfterStatusChange } from "../utils/backgroundSoundSelection"
import { usePersonaAvatarCustomization } from "./usePersonaAvatarCustomization"
import { usePersonaBackgroundSoundActions } from "./usePersonaBackgroundSoundActions"
import {
  usePersonaFormState,
  type ControlledPersonaFormState,
} from "./usePersonaFormState"
import { usePersonaVoiceConfiguration } from "./usePersonaVoiceConfiguration"

const log = logger("usePersonaFormController")
type BackgroundSoundAcousticSourceType = "ambient" | "point_source"

interface UsePersonaFormControllerOptions {
  selectedPersona: Persona | null
  isOpen: boolean
  controlledForm?: ControlledPersonaFormState
  portableAssetsOnly?: boolean
}

/**
 * Bundles every piece of state the persona form body needs into a single
 * controller object, so the editable form (`PersonaForm`) and the read-only
 * version-history preview (`PersonaVersionHistoryView`) can render the exact same
 * `PersonaFormFields` from the same source of truth. This mirrors how the metric
 * version history reuses `MetricViewerProvider` to seed its read-only sections.
 *
 * It owns form data + display hooks (avatar, voice, background sound) and the
 * field-level handlers, but deliberately not persistence, page header, or submit
 * — those are page-only concerns that stay in `PersonaForm`.
 */
export const usePersonaFormController = ({
  selectedPersona,
  isOpen,
  controlledForm,
  portableAssetsOnly = false,
}: UsePersonaFormControllerOptions) => {
  const [currentlyPlayingAudioId, setCurrentlyPlayingAudioId] = useState<
    string | null
  >(null)
  const [isBackgroundSoundUploadOpen, setIsBackgroundSoundUploadOpen] =
    useState(false)
  const [backgroundSoundUploadFile, setBackgroundSoundUploadFile] =
    useState<File | null>(null)
  const [backgroundSoundUploadName, setBackgroundSoundUploadName] = useState("")
  const [
    backgroundSoundUploadAcousticSourceType,
    setBackgroundSoundUploadAcousticSourceType,
  ] = useState<BackgroundSoundAcousticSourceType | undefined>(undefined)

  const {
    formData,
    hasInitializationError,
    isFormInitialized,
    setFormData,
    handleChange,
    updateMetadata,
    isPromptExpanded,
    setIsPromptExpanded,
  } = usePersonaFormState({ selectedPersona, isOpen, controlledForm })

  const {
    randomColorIndex,
    isFaceSelectorOpen,
    setIsFaceSelectorOpen,
    hairPreviews,
    eyesPreviews,
    lipsPreviews,
    handleFaceCustomization,
    handleRegenerateSeed,
  } = usePersonaAvatarCustomization({
    avatarUrl: formData.avatar_url,
    faceCustomization: formData.face_customization,
    setFormData,
  })

  const {
    metadata,
    isInSilentMode,
    handleVoiceSelection,
    handleLanguageSelection,
  } = usePersonaVoiceConfiguration({
    metadata: formData.metadata,
    setFormData,
  })

  const {
    uploadBackgroundSound,
    updateBackgroundSound,
    selectedBackgroundSoundValue,
    selectedBackgroundSound,
    selectedCustomBackgroundSound,
    activeBuiltInBackgroundSounds,
    activeCustomBackgroundSounds,
    selectedArchivedBackgroundSound,
    customSoundNameDraft,
    setCustomSoundNameDraft,
    isBackgroundSoundUploading,
    isBackgroundSoundUpdating,
  } = usePersonaBackgroundSoundActions({
    isOpen,
    isInSilentMode,
    metadata,
    includeCustomSounds: !portableAssetsOnly,
  })

  const isBuiltIn = Boolean(
    selectedPersona && isBuiltInPersona(selectedPersona),
  )
  const isLegacy = selectedPersona
    ? PersonaDTO.isLegacy(selectedPersona)
    : false
  // Speech fields (prompt, voice, call environment, call mechanics) stay on
  // screen in Silent Mode but cannot be edited, since a silent persona never
  // uses them. Hiding them made the form appear to lose its content.
  const isSpeechDisabled = isBuiltIn || isInSilentMode

  const handleBackgroundSoundUpload = useCallback(async () => {
    if (!backgroundSoundUploadFile) {
      toast.danger({
        title: "Choose an audio file",
        description: "Upload a WAV or MP3 file to use as background noise.",
      })
      return
    }

    try {
      const uploadedSound = await uploadBackgroundSound({
        file: backgroundSoundUploadFile,
        displayName: backgroundSoundUploadName,
        acousticSourceType: backgroundSoundUploadAcousticSourceType,
      })
      updateMetadata({
        background_sound: uploadedSound.value,
        background_sound_volume: undefined,
      })
      setCurrentlyPlayingAudioId(null)
      setBackgroundSoundUploadFile(null)
      setBackgroundSoundUploadName("")
      setBackgroundSoundUploadAcousticSourceType(undefined)
      setIsBackgroundSoundUploadOpen(false)
      toast.success({
        title: "Background sound uploaded",
        description: "The sound is selected for this persona.",
      })
    } catch (error) {
      log.error("Error uploading background sound", error)
      toast.error({
        error,
        fallback: "Upload a WAV or MP3 file and try again.",
        title: "Error uploading background sound",
      })
    }
  }, [
    backgroundSoundUploadFile,
    backgroundSoundUploadAcousticSourceType,
    backgroundSoundUploadName,
    updateMetadata,
    uploadBackgroundSound,
  ])

  const handleRenameBackgroundSound = useCallback(async () => {
    if (!selectedCustomBackgroundSound) {
      return
    }

    const trimmedName = customSoundNameDraft.trim()
    if (!trimmedName) {
      toast.danger({
        title: "Name required",
        description: "Custom background sounds need a display name.",
      })
      return
    }

    try {
      // The display name is the only thing a persona may change. A sound's
      // acoustic source type is fixed at upload and shared by every persona
      // using it, so it is deliberately not patchable from here.
      if (trimmedName === selectedCustomBackgroundSound.display_name) {
        return
      }

      await updateBackgroundSound({
        id: selectedCustomBackgroundSound.id,
        display_name: trimmedName,
      })
      toast.success({
        title: "Background sound renamed",
        description: "The custom sound name has been updated.",
      })
    } catch (error) {
      log.error("Error updating background sound", error)
      toast.error({
        error,
        fallback: "Something went wrong. Please try again.",
        title: "Error updating background sound",
      })
    }
  }, [
    customSoundNameDraft,
    selectedCustomBackgroundSound,
    updateBackgroundSound,
  ])

  const handleArchiveBackgroundSound = useCallback(
    async (status: "active" | "archived") => {
      if (!selectedCustomBackgroundSound) {
        return
      }

      try {
        await updateBackgroundSound({
          id: selectedCustomBackgroundSound.id,
          status,
        })
        const selectionPatch =
          getBackgroundSoundSelectionPatchAfterStatusChange({
            currentBackgroundSoundValue: selectedBackgroundSoundValue,
            selectedSoundValue: selectedCustomBackgroundSound.value,
            status,
          })
        if (Object.keys(selectionPatch).length > 0) {
          setCurrentlyPlayingAudioId(null)
          updateMetadata(selectionPatch)
        }
        toast.success({
          title:
            status === "archived"
              ? "Background sound archived"
              : "Background sound restored",
          description:
            status === "archived"
              ? "It was removed from this persona and hidden from new selection."
              : "It is available for selection again.",
        })
      } catch (error) {
        log.error("Error updating background sound status", error)
        toast.error({
          error,
          fallback: "Something went wrong. Please try again.",
          title: "Error updating background sound",
        })
      }
    },
    [
      selectedBackgroundSoundValue,
      selectedCustomBackgroundSound,
      updateBackgroundSound,
      updateMetadata,
    ],
  )

  return {
    selectedPersona,
    portableAssetsOnly,
    isBuiltIn,
    isSpeechDisabled,
    isLegacy,
    // form state
    formData,
    hasInitializationError,
    isFormInitialized,
    setFormData,
    handleChange,
    updateMetadata,
    isPromptExpanded,
    setIsPromptExpanded,
    // voice configuration
    metadata,
    isInSilentMode,
    handleVoiceSelection,
    handleLanguageSelection,
    // avatar customization
    randomColorIndex,
    isFaceSelectorOpen,
    setIsFaceSelectorOpen,
    hairPreviews,
    eyesPreviews,
    lipsPreviews,
    handleFaceCustomization,
    handleRegenerateSeed,
    // background sound
    selectedBackgroundSoundValue,
    selectedBackgroundSound,
    selectedCustomBackgroundSound,
    activeBuiltInBackgroundSounds,
    activeCustomBackgroundSounds,
    selectedArchivedBackgroundSound,
    customSoundNameDraft,
    setCustomSoundNameDraft,
    isBackgroundSoundUploading,
    isBackgroundSoundUpdating,
    currentlyPlayingAudioId,
    setCurrentlyPlayingAudioId,
    isBackgroundSoundUploadOpen,
    setIsBackgroundSoundUploadOpen,
    backgroundSoundUploadFile,
    setBackgroundSoundUploadFile,
    backgroundSoundUploadName,
    setBackgroundSoundUploadName,
    backgroundSoundUploadAcousticSourceType,
    setBackgroundSoundUploadAcousticSourceType,
    handleBackgroundSoundUpload,
    handleRenameBackgroundSound,
    handleArchiveBackgroundSound,
  }
}

export type PersonaFormController = ReturnType<typeof usePersonaFormController>
